import { ReactNode } from 'react';
import { CommanderThunkAction } from '@mews-commander/types';
import { RouteName } from '@mews-commander/enums';
import { AssistantServiceOrderState } from '@mews-commander/data-access';
import { SearchSubSection, SearchType } from '../../assistantSearchTypes';
import { MergedSearchAndNavigationResult } from '../results/ResultListAndNavigations';
import { FilterStateProps } from '../../Assistant';
import { CustomerFilter } from '../../assistantSearchTypeFilters';
import { useReservationsWithDKReady } from '../../hooks/useReservationsWithDKReady';
import { useReservationList } from './useReservationList';
import { useBillList } from './useBillList';
import { useCustomerList } from './useCustomerList';
import { useResourceBlockList } from './useResourceBlockList';
import { useCompanyList } from './useCompanyList';
import { useProductServiceOrderList } from './useProductServiceOrderList';
import { useReservationGroupList } from './useReservationGroupList';
import { useResourceList } from './useResourceList';
import { useTravelAgencyList } from './useTravelAgencyList';
import { useNavigationList } from './useNavigationList';
import { useAvailabilityBlockList } from './useAvailabilityBlockList';
import { useCommandList } from './useCommandList';
import { useRoutingRuleList } from './useRoutingRuleList';

export interface AssistantSearchListItemProps {
    primaryText: string | ReactNode;
    secondaryText?: string | ReactNode;
    leftIcon?: ReactNode;
    rightItem?: ReactNode;
    itemId: string | RouteName;
    routeAction?: CommanderThunkAction;
    value?: string;
    searchType?: SearchType | SearchSubSection;
    'data-test-id'?: string;
}

export const useAssistantSearchResultListMap = (
    assistantSearchResult: MergedSearchAndNavigationResult,
    searchTerm: string,
    filterState: FilterStateProps
): Record<SearchType | SearchSubSection, Array<AssistantSearchListItemProps>> => {
    const {
        reservations,
        bills,
        customers,
        resourceBlocks,
        companies,
        productServiceOrders,
        reservationGroups,
        resources,
        routingRules,
        travelAgencies,
        availabilityBlocks,
        navigations,
    } = assistantSearchResult;
    const { firstLevel, secondLevel } = filterState;

    // Get reservations with DK ready to show status in the result list
    const reservationIds = assistantSearchResult?.reservations?.map((reservation) => reservation.id) || [];
    const reservationsWithDKReady = useReservationsWithDKReady(reservationIds);

    let filteredReservations = reservations;
    let filteredBills = bills;
    let filteredCustomers = customers;
    let filteredCompanies = companies;
    let filteredResources = resources;
    switch (firstLevel) {
        case SearchType.Reservation:
            if (secondLevel) {
                filteredReservations = reservations.filter(
                    (reservation) =>
                        (reservation.timeState === secondLevel &&
                            reservation.serviceOrderState !== AssistantServiceOrderState.Canceled) ||
                        reservation.serviceOrderState === secondLevel
                );
            }
            break;

        case SearchType.Bill:
            if (secondLevel) {
                filteredBills = bills.filter((bill) => bill.state === secondLevel || bill.type === secondLevel);
            }
            break;
        case SearchType.Customer:
            if (secondLevel) {
                filteredCustomers = customers.filter((customer) => {
                    if (secondLevel === CustomerFilter.isVip) {
                        return customer.isVip;
                    } else if (secondLevel === CustomerFilter.isInHouse) {
                        return customer.isInHouse;
                    } else if (secondLevel === CustomerFilter.isPaymaster) {
                        return customer.isPaymaster;
                    }
                    return false;
                });
            }
            break;
        case SearchType.Company:
            if (secondLevel) {
                filteredCompanies = companies.filter((company) => company.isTravelAgency);
            }
            break;
        case SearchType.Resource:
            if (secondLevel) {
                filteredResources = resources.filter((resource) => resource.state === secondLevel);
            }
            break;
        default:
            break;
    }

    const reservationSearchList = useReservationList({
        reservationsWithDKReady,
        reservations: secondLevel ? filteredReservations : reservations,
        searchTerm,
        selectedFilter: firstLevel,
    });
    const billSearchList = useBillList({ bills: filteredBills, searchTerm });
    const customerSearchList = useCustomerList({
        customers: filteredCustomers,
        searchTerm,
        selectedFilter: firstLevel,
    });
    const resourceBlocksSearchList = useResourceBlockList({ resourceBlocks, searchTerm });
    const companySearchList = useCompanyList({ companies: filteredCompanies, searchTerm, selectedFilter: firstLevel });
    const productServiceOrdersSearchList = useProductServiceOrderList({
        productServiceOrderList: productServiceOrders,
        searchTerm,
    });
    const reservationGroupsSearchList = useReservationGroupList({ reservationGroups, searchTerm });
    const resourcesSearchList = useResourceList({ resources: filteredResources, searchTerm });
    const travelAgenciesSearchList = useTravelAgencyList({ travelAgencies, searchTerm });
    const availabilityBlockList = useAvailabilityBlockList({ availabilityBlocks, searchTerm });
    const navigationsSearchList = useNavigationList({ navigations, searchTerm });
    const routingRulesSearchList = useRoutingRuleList({ routingRules, searchTerm });
    const commandsList = useCommandList(searchTerm);

    const showCommandSection =
        navigationsSearchList.length > 0 ||
        availabilityBlockList.length > 0 ||
        travelAgenciesSearchList.length > 0 ||
        resourcesSearchList.length > 0 ||
        reservationGroupsSearchList.length > 0 ||
        productServiceOrdersSearchList.length > 0 ||
        companySearchList.length > 0 ||
        resourceBlocksSearchList.length > 0 ||
        customerSearchList.length > 0 ||
        billSearchList.length > 0 ||
        reservationSearchList.length > 0 ||
        routingRulesSearchList.length > 0;

    return {
        [SearchType.Reservation]: reservationSearchList,
        [SearchType.Customer]: customerSearchList,
        [SearchType.Company]: companySearchList,
        [SearchType.Bill]: billSearchList,
        [SearchType.ProductServiceOrder]: productServiceOrdersSearchList,
        [SearchType.TravelAgency]: travelAgenciesSearchList,
        [SearchType.ReservationGroup]: reservationGroupsSearchList,
        [SearchType.ResourceBlock]: resourceBlocksSearchList,
        [SearchType.Resource]: resourcesSearchList,
        [SearchType.RoutingRule]: routingRulesSearchList,
        [SearchType.AvailabilityBlock]: availabilityBlockList,
        [SearchType.Navigation]: navigationsSearchList,
        [SearchSubSection.Command]: showCommandSection ? commandsList : [],
        [SearchType.City]: [],
        [SearchType.Locations]: [],
        [SearchType.EnterpriseProfile]: [],
    };
};
